import {useSelector} from "react-redux";
import {selectAllPortfolioTransactions} from "../../../slices/portfolioSlice";
import {appConfigSelector} from "../../../slices/appSlice";
import moment from "moment";
import useRate from "../../../util/useRate";
import {selectAllQuotes} from "../../../slices/quotesSlice";
import {PORTFOLIO_TRANSACTION_TYPE_BUY} from "../../../codes";
import {useMemo} from "react";
import {round} from "../../../util/util";

const getEffectiveQuantity = t => t.type === PORTFOLIO_TRANSACTION_TYPE_BUY ? t.effectiveQuantity : -t.effectiveQuantity;
const getQuantity = t => t.type === PORTFOLIO_TRANSACTION_TYPE_BUY ? t.quantity : -t.quantity;

const sum = (a, b) => a + b;

const sumQuotes = (quotes, field) => Object.values(quotes).reduce((a, b) => a + b[field], 0);

const usePortfolioApp = () => {
  const {portfolioCurrency} = useSelector(appConfigSelector);

  const {getPrice} = useRate();

  const allQuotes = useSelector(selectAllQuotes);
  const allPortfolioTransactions = useSelector(selectAllPortfolioTransactions);

  const quotes = useMemo(() => {
    const now = moment().format("YYYY-MM-DD");

    const quotes = {};

    allQuotes.forEach(quote => quotes[quote.symbol] = {...quote, transactions: []});
    allPortfolioTransactions.forEach(o => quotes[o.symbol].transactions.push(o));

    Object.values(quotes).forEach(quote => {
      const transactions = quote.transactions;

      const todayFees = transactions
        .filter(t => t.valueDate === now)
        .map(t => t.fees)
        .reduce(sum, 0);

      const dayOpenValue = transactions
        .map(t => getEffectiveQuantity(t) * (t.valueDate === now ? t.price : quote.previousClose))
        .reduce(sum, 0);

      quote.quantity = round(transactions.map(getEffectiveQuantity).reduce(sum, 0), 10);
      quote.marketValue = getPrice(quote.quantity * quote.price, quote.currency, portfolioCurrency);
      quote.initialValue = transactions.map(t => (getQuantity(t) * t.price * t.rate) + (t.fees || 0)).reduce(sum, 0);
      quote.dayOpenValue = getPrice(dayOpenValue, quote.currency, portfolioCurrency) + todayFees;
      quote.dayGains = quote.marketValue - quote.dayOpenValue;
      quote.ongoingGains = quote.marketValue - quote.initialValue;

      const buyTransactions = transactions.filter(t => t.type === PORTFOLIO_TRANSACTION_TYPE_BUY);

      quote.pru = buyTransactions.map(p => p.price * p.quantity).reduce(sum, 0) / buyTransactions.map(getEffectiveQuantity).reduce(sum, 0);
    });

    return quotes;
  }, [allQuotes, allPortfolioTransactions, getPrice, portfolioCurrency]);

  const marketValue = sumQuotes(quotes, "marketValue");
  const initialValue = sumQuotes(quotes, "initialValue");
  const dayGains = sumQuotes(quotes, "dayGains");
  const ongoingGains = sumQuotes(quotes, "ongoingGains");

  return {quotes, marketValue, initialValue, dayGains, ongoingGains};
};

export default usePortfolioApp;
